import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { MsgModel } from '../../common-components/msg/model/msg.model';
import { Organization } from '../../common-components/organizations/model/organization.model';
import { EmailDomains } from '../model/email-domains.model';
import { EmailDomainsService } from '../service/email-domains.service';

@Component({
    selector: 'app-edit-email-domains',
    templateUrl: './edit-email-domains.component.html',
    styleUrls: ['./edit-email-domains.component.scss']
})
export class EditEmailDomainsComponent implements OnInit {
    @ViewChildren(MatPaginator) paginators: QueryList<MatPaginator> = new QueryList<MatPaginator>();
    @ViewChildren(MatSort) sorts: QueryList<MatSort> = new QueryList<MatSort>();

    // Search variable
    searchString: string = '';

    // Table display
    displayedColumns: string[] = ['EDIT', 'EMAIL_DOMAIN', 'ORGANIZATION.NAME'];
    newDisplayedColumns: string[] = ['ADD'];

    // Table results
    tableDataSources: MatTableDataSource<EmailDomains>[] = [];

    // Results
    results: EmailDomains[] = [];
    displayName: string = '';
    filter: string = '';

    isLoading: boolean = false;
    msg: MsgModel = {};

    getPropertyPipe = new GetPropertyPipe();

    // Editing variables
    addingForm!: UntypedFormGroup;
    adding: boolean = false;
    editingForm!: UntypedFormGroup;
    editingId: number | undefined;

    emailDomainOrganization: Organization;

    constructor(private fb: UntypedFormBuilder, private emailDomainsService: EmailDomainsService) {
        this.addingForm = fb.group({
            emailDomain: ['', [Validators.required]]
        });
        this.editingForm = fb.group({
            emailDomain: ['', [Validators.required]]
        });
    }

    ngOnInit(): void {}

    indexEmailDomains() {
        this.isLoading = true;
        this.searchString = '';

        this.emailDomainsService.index().subscribe({
            next: (res) => {
                this.results = res.emailDomains;
            },
            error: (err) => {
                this.results = [];
                this.msg = { SUCCESS: false, MSG: 'Error retrieving Email Domains.' };
            },
            complete: () => {
                // Updates the Tables to be displayed w/ this.results
                this.updateDataSource();

                // Display results
                this.isLoading = false;
            }
        });
    }

    searchEmailDomains(searchString: string) {
        if (searchString.length > 2) {
            this.isLoading = true;
            this.filter = '';

            this.emailDomainsService.search(searchString).subscribe({
                next: (res) => {
                    this.results = res.emailDomains;

                    if (res.msg) {
                        this.msg = res.msg;
                    }
                },
                error: (err) => {
                    this.results = [];
                    this.msg = { SUCCESS: false, MSG: 'Error searching Email Domains.' };
                },
                complete: () => {
                    // Updates the Tables to be displayed w/ this.results
                    this.updateDataSource();

                    // Display results
                    this.isLoading = false;
                }
            });
        }
    }

    addNewEmailDomain(emailDomain: EmailDomains) {
        let newEmailDomain = emailDomain;
        this.emailDomainsService.createEmailDomain(emailDomain).subscribe({
            next: (res) => {
                this.msg = res.msg;

                // Set ID from newly added
                newEmailDomain = res.newEmailDomain;
            },
            error: (err) => {
                this.msg = { SUCCESS: false, MSG: 'Error adding new Email Domain.' };
            },
            complete: () => {
                this.results.push(newEmailDomain);

                this.updateDataSource();
            }
        });
    }

    updateEmailDomain(emailDomain: EmailDomains) {
        this.emailDomainsService.updateEmailDomain(emailDomain).subscribe({
            next: (res) => {
                this.msg = res.msg;
            },
            error: (err) => {
                this.msg = { SUCCESS: false, MSG: 'Error updating new Email Domain.' };
            },
            complete: () => {
                let index = this.results.findIndex((res) => res.ID == emailDomain.ID);
                this.results[index] = emailDomain;

                this.updateDataSource();
            }
        });
    }

    deleteSelectedEmailDomain(emailDomain: EmailDomains) {
        this.emailDomainsService.deleteEmailDomain(emailDomain).subscribe({
            next: (res) => {
                this.msg = res.msg;
            },
            error: (err) => {
                this.msg = { SUCCESS: false, MSG: 'Error deleting new Email Domain.' };
            },
            complete: () => {
                let index = this.results.findIndex((res) => res.ID == emailDomain.ID);
                this.results.splice(index, 1);

                this.updateDataSource();
            }
        });
    }

    updateDataSource() {
        this.tableDataSources = [];

        setTimeout(() => {
            // Data
            let dataSource = new MatTableDataSource<EmailDomains>();
            dataSource.data = this.results;

            // Paginator
            dataSource.paginator = this.paginators.toArray()[0];

            // Sort
            dataSource.sort = this.sorts.toArray()[0];
            const defaultSort: Sort = { active: 'NAME', direction: 'asc' };
            dataSource.sort.active = defaultSort.active;
            dataSource.sort.direction = defaultSort.direction;
            dataSource.sort.sortChange.emit(defaultSort);
            dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

            // Filter
            dataSource.filterPredicate = (data: any, filter) => {
                const dataStr = JSON.stringify(data).toLowerCase();
                return dataStr.indexOf(filter) != -1;
            };

            this.tableDataSources.push(dataSource);

            if (this.filter) {
                this.setFilter();
            }
        });
    }

    setFilter() {
        this.tableDataSources.forEach((dataSource, index) => {
            dataSource.filter = this.filter.trim().toLocaleLowerCase();
        });
    }

    // Valid Functions
    emailDomainOrganizationValid(): boolean {
        if (this.emailDomainOrganization.NAME) {
            if (this.emailDomainOrganization.NAME?.length > 0) {
                if (this.emailDomainOrganization.ID! > 0) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        } else {
            return true;
        }
    }

    // Add Function
    addEmailDomain() {
        this.newDisplayedColumns = ['ADD_EDIT', 'ADD_EMAIL_DOMAIN', 'ADD_ORGANIZATION'];
        this.resetAddingEmailDomain();
        this.adding = true;
    }
    confirmAddEmailDomain() {
        this.addNewEmailDomain(this.buildEmailDomain(this.addingForm));
        this.cancelAdd();
    }
    cancelAddEmailDomain() {
        this.cancelAdd();
    }

    // Edit Function
    editEmailDomain(emailDomain: EmailDomains) {
        this.setEditingEmailDomain(emailDomain);
    }
    confirmEditEmailDomain() {
        this.updateEmailDomain(this.buildEmailDomain(this.editingForm));
        this.cancelEdit();
    }
    cancelEditEmailDomain() {
        this.cancelEdit();
    }

    // Delete Function
    deleteEmailDomain(emailDomain: EmailDomains) {
        this.deleteSelectedEmailDomain(emailDomain);
    }

    // Reset Function
    private cancelAdd() {
        this.resetAddingEmailDomain();
        this.newDisplayedColumns = ['ADD'];
        this.adding = false;
    }

    private cancelEdit() {
        this.resetEditingEmailDomain();
    }

    private setEditingEmailDomain(emailDomain: EmailDomains) {
        this.editingId = emailDomain.ID;
        this.editingForm.patchValue({ emailDomain: emailDomain.EMAIL_DOMAIN });
        this.editingForm.markAsPristine();
        console.log(emailDomain);
        if (emailDomain.ORGANIZATION) {
            if (emailDomain.ORGANIZATION.ID! > 0) {
                this.emailDomainOrganization = emailDomain.ORGANIZATION;
            } else {
                this.emailDomainOrganization = new Organization();
                this.emailDomainOrganization.NAME = '';
            }
        } else {
            this.emailDomainOrganization = new Organization();
            this.emailDomainOrganization.NAME = '';
        }
    }

    private resetAddingEmailDomain() {
        this.addingForm.patchValue({ emailDomain: '' });
        this.addingForm.markAsPristine();
        this.emailDomainOrganization = new Organization();
        this.emailDomainOrganization.NAME = '';
    }

    private resetEditingEmailDomain() {
        this.editingForm.patchValue({ emailDomain: '' });
        this.editingId = undefined;
        this.emailDomainOrganization = new Organization();
    }

    private buildEmailDomain(form: UntypedFormGroup): EmailDomains {
        let newEmailDomain = new EmailDomains();

        newEmailDomain.ID = this.editingId ? this.editingId : undefined;
        newEmailDomain.EMAIL_DOMAIN = form.get('emailDomain')?.value;
        newEmailDomain.ORGANIZATION = this.emailDomainOrganization.ID ? this.emailDomainOrganization : undefined;
        newEmailDomain.ORGANIZATIONS_ID = this.emailDomainOrganization.ID ? this.emailDomainOrganization.ID : undefined;

        return newEmailDomain;
    }
}
